import React, { useState } from 'react';
import { Box, Button } from '@ublend-npm/aulaui-core';
import EastOutlinedIcon from '@mui/icons-material/EastOutlined';
import SyncOutlinedIcon from '@mui/icons-material/SyncOutlined';
import Divider from '@mui/material/Divider';
import OutlookEmailRow from './components/OutlookEmailRow';
import OutlookEmailListSkeleton from './components/OutlookEmailListSkeleton';
import OutlookEmptyState from './components/OutlookEmptyState';

// Re-export types so existing imports from this file keep working.
export type {
  OutlookMessage,
  OutlookInboxData,
  OutlookThread,
  OutlookThreadMessage,
} from './types';

const PREVIEW_EMAIL_COUNT = 4;

type OutlookInboxProps = {
  messages: import('./types').OutlookMessage[];
  isLoading?: boolean;
  onViewAll: () => void;
  /** Called with the email's own `id` (not the conversationId/threadId). */
  onMessageClick: (emailId: string) => void;
  onRefresh: () => void;
};

const OutlookInbox = ({
  messages,
  isLoading = false,
  onViewAll,
  onMessageClick,
  onRefresh,
}: OutlookInboxProps) => {
  const [selectedMessageId, setSelectedMessageId] = useState<string | null>(
    null
  );
  const visibleMessages = messages.slice(0, PREVIEW_EMAIL_COUNT);
  const hasMoreEmails = messages.length > PREVIEW_EMAIL_COUNT;

  const handleSelect = (message: import('./types').OutlookMessage) => {
    setSelectedMessageId(message.id);
    onMessageClick(message.id);
  };

  if (isLoading) {
    return <OutlookEmailListSkeleton count={PREVIEW_EMAIL_COUNT} />;
  }

  return (
    <Box display="flex" flexDirection="column" flex={1} minHeight={0}>
      <Box
        flex={1}
        minHeight={0}
        display={messages.length === 0 ? 'flex' : 'block'}
        sx={{
          maxHeight: '440px',
          overflowY: 'auto',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
        {messages.length === 0 ? (
          <OutlookEmptyState />
        ) : (
          visibleMessages.map((message, index) => (
            <React.Fragment key={message.id}>
              <OutlookEmailRow
                message={message}
                isSelected={selectedMessageId === message.id}
                onClick={handleSelect}
              />
              {index < visibleMessages.length - 1 && (
                <Divider sx={{ width: '100%', mx: 0, my: 1 }} />
              )}
            </React.Fragment>
          ))
        )}
      </Box>

      <Box
        display="flex"
        gap={1.5}
        mt="auto"
        sx={{
          flexDirection: { xs: 'column', sm: 'row' },
        }}
      >
        <Button
          kind="secondary"
          onClick={onRefresh}
          sx={{
            flex: 1,
            border: 'none',
            boxShadow: 'none',
            backgroundColor: 'transparent',
            color: 'primary.main',
            '&:hover': { border: 'none' },
          }}
          startIcon={
            <SyncOutlinedIcon
              sx={{
                color: 'primary.main !important',
                fontSize: 22,
                transform: 'rotate(90deg)',
              }}
            />
          }
        >
          Sync now
        </Button>

        {hasMoreEmails ? (
          <Button
            kind="secondary"
            onClick={onViewAll}
            sx={{ flex: 1, height: 42, minHeight: 42 }}
            endIcon={<EastOutlinedIcon sx={{ fontSize: 24 }} />}
          >
            View all
          </Button>
        ) : null}
      </Box>
    </Box>
  );
};

export default OutlookInbox;
